import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useWidgets } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle, XCircle, Copy, ExternalLink, Loader2, FlaskConical, Webhook, Shield, AlertTriangle, Check, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const CheckoutPage = () => {
  const { invoiceId } = useParams();
  const { payment_widget } = useWidgets();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(0);
  const [step, setStep] = useState(1);
  const [verifyTxHash, setVerifyTxHash] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [webhookResults, setWebhookResults] = useState(null);
  const [transactionDetected, setTransactionDetected] = useState(false);
  const [showManualVerify, setShowManualVerify] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [emailSaved, setEmailSaved] = useState(false);
  const manualVerifyTimerRef = useRef(null);
  const pollingRef = useRef(null);

  // API-source network/token selection state
  const [needsSelection, setNeedsSelection] = useState(false);
  const [paymentOptions, setPaymentOptions] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [selectedToken, setSelectedToken] = useState(null);
  const [updatingMethod, setUpdatingMethod] = useState(false);

  useEffect(() => { document.title = 'Checkout | CoinPayUp'; }, []);

  // Inject payment widget when enabled
  useEffect(() => {
    if (!payment_widget?.enabled || !payment_widget?.embed_code) return;
    const wrapper = document.createElement('div');
    wrapper.innerHTML = payment_widget.embed_code;
    const scripts = wrapper.querySelectorAll('script');
    const injected = [];
    scripts.forEach(s => {
      const el = document.createElement('script');
      if (s.src) el.src = s.src; else el.textContent = s.textContent;
      el.async = true;
      document.body.appendChild(el);
      injected.push(el);
    });
    return () => injected.forEach(el => el.remove());
  }, [payment_widget?.enabled, payment_widget?.embed_code]);

  const saveCustomerEmail = async () => {
    if (!customerEmail || !customerEmail.includes('@')) return;
    try {
      await axios.put(`${API}/invoices/${invoiceId}/customer-email`, { customer_email: customerEmail });
      localStorage.setItem('coinpayup_customer_email', customerEmail);
      setEmailSaved(true);
    } catch {}
  };

  useEffect(() => {
    fetchInvoice();
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); if (manualVerifyTimerRef.current) clearTimeout(manualVerifyTimerRef.current); };
  }, [invoiceId]);

  useEffect(() => {
    if (invoice?.expires_at) {
      const updateTimer = () => {
        const now = new Date();
        const expiry = new Date(invoice.expires_at);
        const diff = Math.max(0, Math.floor((expiry - now) / 1000));
        setTimeLeft(diff);
      };
      updateTimer();
      const timer = setInterval(updateTimer, 1000);
      return () => clearInterval(timer);
    }
  }, [invoice?.expires_at]);

  const isExactAmount = invoice?.verification_method === 'exact_amount';

  // Start polling on step 4 (all methods) and step 5 (non-exact_amount) and partially_paid
  useEffect(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    const isPartial = invoice?.status === 'partially_paid';
    const shouldPoll = ((step === 4 || (!isExactAmount && step === 5)) && invoice?.status === 'pending') || isPartial;
    if (shouldPoll) {
      pollingRef.current = setInterval(fetchInvoice, 5000);
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [step, invoice?.status, isExactAmount]);

  // 3-minute timer to show "I have sent payment" button on step 4
  useEffect(() => {
    if (manualVerifyTimerRef.current) clearTimeout(manualVerifyTimerRef.current);
    if (step === 4) {
      setShowManualVerify(false);
      manualVerifyTimerRef.current = setTimeout(() => {
        setShowManualVerify(true);
      }, 180000); // 3 minutes
    } else {
      setShowManualVerify(false);
    }
    return () => { if (manualVerifyTimerRef.current) clearTimeout(manualVerifyTimerRef.current); };
  }, [step]);

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}`);
      setInvoice(response.data);
      if (response.data.customer_email && !customerEmail) {
        setCustomerEmail(response.data.customer_email);
        setEmailSaved(true);
      } else if (!response.data.customer_email && !customerEmail) {
        const saved = localStorage.getItem('coinpayup_customer_email');
        if (saved) setCustomerEmail(saved);
      }
      if (response.data.transaction_detected) {
        setTransactionDetected(true);
      }
      // Only check for network/token selection on pending API invoices
      if (response.data.status === 'pending') {
        const isApiSource = response.data.source === 'api' || response.data.source === 'payment_link';
        const alreadySelected = response.data.payment_method_selected;
        if (isApiSource && !alreadySelected && !needsSelection && step === 1) {
          setNeedsSelection(true);
          try {
            const optRes = await axios.get(`${API}/invoices/${invoiceId}/payment-options`);
            const opts = optRes.data;
            setPaymentOptions(opts);
            const nets = opts.networks || [];
            const toks = opts.tokens || [];

            if (nets.length === 0) {
              // No networks available, stay on step 1
              setNeedsSelection(false);
              return;
            }

            if (nets.length === 1) {
              // Auto-select the only network
              const autoNet = nets[0];
              setSelectedNetwork(autoNet.id);
              const tokensForNet = toks.filter(t => t.network_id === autoNet.id);

              if (tokensForNet.length <= 1) {
                // Auto-select the only token and confirm immediately
                const autoTok = tokensForNet[0]?.symbol || response.data.currency;
                setSelectedToken(autoTok);
                // Confirm directly — skip both screens
                try {
                  await axios.put(`${API}/invoices/${invoiceId}/payment-method`, {
                    network: autoNet.id,
                    token: autoTok,
                  });
                  setNeedsSelection(false);
                  setStep(1);
                  const res = await axios.get(`${API}/invoices/${invoiceId}`);
                  setInvoice(res.data);
                } catch {
                  // Fallback: show token selection
                  setStep(-1);
                }
                return;
              }
              // Multiple tokens — skip to token selection
              setStep(-1);
            } else {
              // Multiple networks — show network selection
              if (opts.default_network_id) {
                setSelectedNetwork(opts.default_network_id);
              }
              setStep(-2);
            }
          } catch { /* silent */ }
        }
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Address copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const confirmPaymentMethod = async (networkId, tokenSymbol) => {
    const netId = networkId || selectedNetwork;
    const tokSym = tokenSymbol || selectedToken;
    if (!netId || !tokSym) return;
    const netObj = paymentOptions?.networks?.find(n => n.id === netId);
    if (!netObj) { toast.error('Network not found'); return; }
    setUpdatingMethod(true);
    try {
      await axios.put(`${API}/invoices/${invoiceId}/payment-method`, {
        network: netObj.id,
        token: tokSym,
      });
      setNeedsSelection(false);
      setStep(1);
      try {
        const res = await axios.get(`${API}/invoices/${invoiceId}`);
        setInvoice(res.data);
      } catch { /* silent */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update payment method');
    } finally {
      setUpdatingMethod(false);
    }
  };

  const availableTokensForNetwork = paymentOptions?.tokens?.filter(t => t.network_id === selectedNetwork) || [];
  const selectedNetworkObj = paymentOptions?.networks?.find(n => n.id === selectedNetwork);

  const simulatePayment = async () => {
    setSimulating(true);
    try {
      await axios.post(`${API}/invoices/${invoiceId}/simulate-payment`);
      toast.success('Test payment simulated!');
      // Direct re-fetch to update UI
      try {
        const res = await axios.get(`${API}/invoices/${invoiceId}`);
        setInvoice(res.data);
      } catch { /* silent */ }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Simulation failed');
    } finally {
      setSimulating(false);
    }
  };

  const testWebhook = async () => {
    setTestingWebhook(true);
    setWebhookResults(null);
    try {
      const response = await axios.post(`${API}/webhooks/test-invoice/${invoiceId}`);
      setWebhookResults(response.data.results);
      const allDelivered = response.data.results.every(r => r.status === 'delivered');
      if (allDelivered) toast.success(`Webhook delivered to ${response.data.results.length} endpoint(s)!`);
      else toast.warning('Some webhook deliveries failed.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Webhook test failed');
    } finally {
      setTestingWebhook(false);
    }
  };

  const [verifyMode, setVerifyMode] = useState('sender_wallet'); // 'sender_wallet' or 'tx_id'
  const [senderWallet, setSenderWallet] = useState('');

  const verifyBySenderWallet = async () => {
    if (!senderWallet.trim()) { toast.error('Please enter a sender wallet address'); return; }
    setVerifying(true);
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/verify-by-wallet`, { sender_wallet: senderWallet.trim() });
      const result = response.data;
      if (result.status === 'paid' || result.status === 'overpaid') {
        toast.success(result.message);
      } else if (result.status === 'partially_paid') {
        toast.warning(result.message);
      } else if (result.status === 'underpaid') {
        toast.warning(result.message);
      } else if (result.status === 'unpaid') {
        toast.error(result.message);
      }
      setSenderWallet('');
      fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const verifyPayment = async () => {
    if (!verifyTxHash.trim()) { toast.error('Please enter a transaction hash'); return; }
    setVerifying(true);
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/verify-payment`, { tx_hash: verifyTxHash.trim() });
      const result = response.data;
      if (result.status === 'paid' || result.status === 'overpaid') {
        toast.success(result.message);
      } else if (result.status === 'partially_paid') {
        toast.warning(result.message);
      } else if (result.status === 'underpaid') {
        toast.warning(result.message);
      } else if (result.status === 'unpaid') {
        toast.error(result.message);
      }
      setVerifyTxHash('');
      fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const timerProgress = invoice?.expires_at
    ? Math.max(0, Math.min(100, (timeLeft / 1800) * 100))
    : 0;

  // Auto-redirect after payment confirmed or expired/cancelled
  const [redirecting, setRedirecting] = useState(false);
  const isPaidForRedirect = invoice?.status === 'paid' || invoice?.status === 'overpaid';
  const isExpiredForRedirect = invoice?.status === 'expired' || invoice?.status === 'cancelled';
  useEffect(() => {
    if (!invoice || redirecting) return;
    const successUrl = invoice.success_url;
    const cancelUrl = invoice.cancel_url;
    if (isPaidForRedirect && successUrl) {
      setRedirecting(true);
      const timer = setTimeout(() => {
        const url = new URL(successUrl);
        url.searchParams.set('invoice_id', invoice.id);
        url.searchParams.set('status', 'paid');
        window.location.href = url.toString();
      }, 3000);
      return () => clearTimeout(timer);
    }
    // For billing invoices with no success_url, show a link to dashboard (don't auto-redirect — user may not be logged in)
    if (isPaidForRedirect && !successUrl && invoice.source === 'billing') {
      setRedirecting(true);
      // No auto-redirect to avoid blank page for unauthenticated sessions
    }
    if (isExpiredForRedirect && cancelUrl) {
      setRedirecting(true);
      const timer = setTimeout(() => {
        const url = new URL(cancelUrl);
        url.searchParams.set('invoice_id', invoice.id);
        url.searchParams.set('status', invoice.status);
        window.location.href = url.toString();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isPaidForRedirect, isExpiredForRedirect, invoice?.success_url, invoice?.cancel_url, invoice?.source]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center" data-testid="checkout-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#6c63ff]" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4" data-testid="checkout-not-found">
        <div className="max-w-sm w-full text-center">
          <XCircle className="h-10 w-10 text-white/20 mx-auto mb-6" />
          <h2 className="text-lg font-medium text-white mb-2">Invoice not found</h2>
          <p className="text-white/40 text-sm mb-8">This invoice may have been deleted or doesn't exist.</p>
          <Link to="/">
            <Button className="bg-white text-black hover:bg-white/90 rounded-md px-6 h-10 text-sm font-medium">
              Go Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const isPending = invoice.status === 'pending';
  const isPaid = invoice.status === 'paid' || invoice.status === 'overpaid';
  const isPartiallyPaid = invoice.status === 'partially_paid';
  const isUnderpaid = invoice.status === 'underpaid';
  const isUnpaid = invoice.status === 'unpaid';
  const isExpired = invoice.status === 'expired' || (isPending && timeLeft <= 0);
  const displayAmount = invoice.payable_amount || invoice.amount;

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center px-4 py-12" data-testid="checkout-page">
      {/* Test Mode Banner */}
      {invoice.is_test && (
        <div className="w-full max-w-[460px] mb-4" data-testid="checkout-test-banner">
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500/8 border border-amber-500/15">
            <FlaskConical className="h-3.5 w-3.5 text-amber-400 shrink-0" />
            <span className="text-amber-400/90 text-xs font-medium tracking-wide">TEST MODE — No real payment required</span>
          </div>
        </div>
      )}

      <div className="w-full max-w-[460px]">
        <div className="bg-[#141414] border border-white/[0.08] rounded-2xl overflow-hidden" data-testid="checkout-card">

          {/* === NETWORK/TOKEN SELECTION FOR API INVOICES === */}
          {step === -2 && paymentOptions && (
            <>
              <div className="px-7 pt-7 pb-5">
                <p className="text-white/40 text-sm mb-1">Payment Amount</p>
                <p className="text-white text-3xl font-bold tracking-tight" data-testid="checkout-select-amount">
                  {invoice.amount}
                </p>
                {invoice.description && <p className="text-white/30 text-sm mt-2">{invoice.description}</p>}
              </div>
              <div className="h-px bg-white/[0.06]" />
              <div className="px-7 py-6" data-testid="checkout-step-network">
                <h3 className="text-white text-lg font-semibold mb-1">Select Network</h3>
                <p className="text-white/40 text-sm mb-5">Choose the blockchain network for your payment</p>
                <div className="space-y-2.5 mb-6">
                  {paymentOptions.networks.map((n) => (
                    <button key={n.id} onClick={() => { setSelectedNetwork(n.id); setSelectedToken(null); }}
                      className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${
                        selectedNetwork === n.id ? 'border-[#6c63ff] bg-[#6c63ff]/[0.08]' : 'border-white/[0.08] bg-white/[0.02] hover:border-white/[0.15]'
                      }`} data-testid={`checkout-network-${n.id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                          selectedNetwork === n.id ? 'bg-[#6c63ff]/20 text-[#6c63ff]' : 'bg-white/[0.06] text-white/50'
                        }`}>{n.symbol?.slice(0, 3)}</div>
                        <div className="text-left">
                          <p className="text-white font-medium text-sm">{n.name}</p>
                          <p className="text-white/30 text-xs">{n.symbol}</p>
                        </div>
                      </div>
                      {selectedNetwork === n.id && <Check className="h-5 w-5 text-[#6c63ff]" />}
                    </button>
                  ))}
                </div>
                <Button className="w-full h-14 bg-[#6c63ff] hover:bg-[#5b54e6] text-white font-semibold rounded-xl text-lg tracking-wide transition-all disabled:opacity-40"
                  disabled={!selectedNetwork} onClick={() => setStep(-1)} data-testid="checkout-next-network">
                  Continue <ChevronRight className="h-5 w-5 ml-1" />
                </Button>
              </div>
            </>
          )}

          {step === -1 && paymentOptions && (
            <>
              <div className="px-7 pt-7 pb-5">
                <p className="text-white/40 text-sm mb-1">Payment Amount</p>
                <p className="text-white text-3xl font-bold tracking-tight">{invoice.amount}</p>
                {invoice.description && <p className="text-white/30 text-sm mt-2">{invoice.description}</p>}
              </div>
              <div className="h-px bg-white/[0.06]" />
              <div className="px-7 py-6" data-testid="checkout-step-token">
                <h3 className="text-white text-lg font-semibold mb-1">Select Token</h3>
                <p className="text-white/40 text-sm mb-5">
                  Choose the token on <span className="text-white/60 font-medium">{selectedNetworkObj?.name}</span>
                </p>
                <div className="space-y-2.5 mb-6">
                  {availableTokensForNetwork.map((t) => (
                    <button key={t.id || t.symbol} onClick={() => setSelectedToken(t.symbol)}
                      className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${
                        selectedToken === t.symbol ? 'border-[#6c63ff] bg-[#6c63ff]/[0.08]' : 'border-white/[0.08] bg-white/[0.02] hover:border-white/[0.15]'
                      }`} data-testid={`checkout-token-${t.symbol}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                          selectedToken === t.symbol ? 'bg-[#6c63ff]/20 text-[#6c63ff]' : 'bg-white/[0.06] text-white/50'
                        }`}>{t.symbol?.slice(0, 4)}</div>
                        <div className="text-left">
                          <p className="text-white font-medium text-sm">{t.name}</p>
                          <p className="text-white/30 text-xs">{t.symbol}</p>
                        </div>
                      </div>
                      {selectedToken === t.symbol && <Check className="h-5 w-5 text-[#6c63ff]" />}
                    </button>
                  ))}
                  {availableTokensForNetwork.length === 0 && (
                    <p className="text-white/30 text-sm text-center py-4">No tokens available for this network</p>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button className="flex-1 h-14 bg-white/[0.1] hover:bg-white/[0.15] text-white font-semibold rounded-xl text-lg transition-all"
                    onClick={() => setStep(-2)} data-testid="checkout-back-token">Back</Button>
                  <Button className="flex-1 h-14 bg-[#6c63ff] hover:bg-[#5b54e6] text-white font-semibold rounded-xl text-lg tracking-wide transition-all disabled:opacity-40"
                    disabled={!selectedToken || updatingMethod} onClick={() => confirmPaymentMethod()} data-testid="checkout-confirm-token">
                    {updatingMethod ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
                    {updatingMethod ? 'Loading...' : 'Proceed to Pay'}
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* === SHARED HEADER === */}
          {step >= 1 && (() => {
            const networkLabels = { bsc: { standard: 'BEP20', name: 'BNB Smart Chain' }, tron: { standard: 'TRC20', name: 'TRON' }, ethereum: { standard: 'ERC20', name: 'Ethereum' }, polygon: { standard: 'Polygon', name: 'Polygon' } };
            const netInfo = networkLabels[invoice?.network] || { standard: invoice?.network?.toUpperCase(), name: invoice?.network };
            return (
          <div className="px-7 pt-7 pb-0">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-baseline gap-3">
                <span className="text-white text-3xl font-light tracking-tight" data-testid="currency-label">{invoice.currency}</span>
                <span className="text-[#6c63ff] text-2xl font-medium tracking-tight">{netInfo.standard}</span>
              </div>
              <StatusPill status={invoice.status} isExpired={isExpired} />
            </div>
          </div>
          );
          })()}

          {step >= 1 && <div className="h-px bg-white/[0.06]" />}

          {/* === SHARED TIMER === */}
          {step >= 1 && isPending && !isExpired && (
            <div className="px-7 pt-5 pb-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/50 text-sm font-medium">Time Remaining</span>
                <span className="text-[#6c63ff] text-sm font-mono font-semibold tabular-nums" data-testid="timer">
                  {formatTime(timeLeft)}
                </span>
              </div>
              <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#6c63ff] rounded-full transition-all duration-1000 ease-linear"
                  style={{ width: `${timerProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* === EXPIRED STATE === */}
          {isExpired && !isPaid && (
            <div className="px-7 py-10">
              <div className="flex items-center justify-center gap-3 p-6 rounded-xl bg-red-500/[0.06] border border-red-500/10">
                <XCircle className="h-5 w-5 text-red-400 shrink-0" />
                <span className="text-red-400 text-lg font-medium">This invoice has expired</span>
              </div>
            </div>
          )}

          {/* === PAID STATE === */}
          {isPaid && (
            <div className="px-7 py-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 mb-6">
                <CheckCircle className="h-8 w-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {invoice.status === 'overpaid' ? 'Payment Received — Overpaid' : 'Payment Confirmed'}
              </h3>
              <p className="text-white/30 text-sm mb-4">
                {invoice.status === 'overpaid'
                  ? 'You sent more than the invoice amount. Merchant has been notified.'
                  : 'Transaction verified on the blockchain.'}
              </p>

              {/* Redirect notice */}
              {redirecting && invoice.success_url && (
                <div className="mb-6 p-5 rounded-xl bg-[#6c63ff]/10 border border-[#6c63ff]/20" data-testid="redirect-notice">
                  <p className="text-white text-lg font-semibold mb-1 animate-pulse">
                    Payment received. Redirecting. Keep this page open.
                  </p>
                </div>
              )}
              {/* For billing invoices — show button instead of auto-redirect */}
              {redirecting && invoice.source === 'billing' && (
                <div className="mb-6">
                  <a href="/dashboard/billing"
                    data-testid="billing-dashboard-btn"
                    className="block w-full text-center py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors">
                    Return to Billing Dashboard
                  </a>
                </div>
              )}

              {/* Amount details */}
              {invoice.received_amount && (
                <div className="mb-4 p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] text-left space-y-2" data-testid="payment-amount-details">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/40">Invoice Amount</span>
                    <span className="text-white/70 font-mono">{invoice.payable_amount || invoice.amount} {invoice.currency}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/40">Received Amount</span>
                    <span className="text-white/70 font-mono">{invoice.received_amount} {invoice.currency}</span>
                  </div>
                  {invoice.tolerance_applied && (
                    <div className="flex justify-between text-sm">
                      <span className="text-white/40">Tolerance Applied</span>
                      <span className="text-amber-400 font-mono">{invoice.tolerance_percentage}%</span>
                    </div>
                  )}
                </div>
              )}

              {invoice.tolerance_applied && (
                <p className="text-amber-400/80 text-xs mb-4 px-3 py-2 rounded-lg bg-amber-500/8 border border-amber-500/10" data-testid="checkout-tolerance-notice">
                  Payment accepted within {invoice.tolerance_percentage}% tolerance.
                </p>
              )}
              {invoice.tx_hash && !invoice.tx_hash.startsWith('0xTEST') && (
                <a href={`https://bscscan.com/tx/${invoice.tx_hash}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-emerald-400 text-sm hover:text-emerald-300 transition-colors" data-testid="view-bscscan-link">
                  View on BSCScan <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {/* Webhook Test (Test mode + Paid) */}
              {invoice.is_test && (
                <div className="mt-6 pt-6 border-t border-white/[0.06] space-y-3">
                  <Button className="w-full h-11 bg-white/[0.06] hover:bg-white/[0.1] text-white font-medium rounded-xl text-sm transition-all border border-white/[0.06]"
                    onClick={testWebhook} disabled={testingWebhook} data-testid="test-webhook-btn">
                    {testingWebhook ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Webhook className="h-4 w-4 mr-2 text-white/50" />}
                    {testingWebhook ? 'Sending...' : 'Test Webhook'}
                  </Button>
                  {webhookResults && (
                    <div className="space-y-2" data-testid="webhook-results">
                      {webhookResults.map((result, i) => (
                        <div key={i} className={`p-3 rounded-lg border text-xs ${result.status === 'delivered' ? 'bg-emerald-500/[0.06] border-emerald-500/10' : 'bg-red-500/[0.06] border-red-500/10'}`}
                          data-testid={`webhook-result-${i}`}>
                          <div className="flex items-center justify-between">
                            <code className="font-mono text-white/40 truncate max-w-[200px]">{result.url}</code>
                            <span className={`font-medium ${result.status === 'delivered' ? 'text-emerald-400' : 'text-red-400'}`}>
                              {result.status === 'delivered' ? `${result.response_code} OK` : 'Failed'}
                            </span>
                          </div>
                          {result.error && <p className="text-red-400/60 mt-1.5">{result.error}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* === UNDERPAID STATE === */}
          {isUnderpaid && (
            <div className="px-7 py-10 text-center" data-testid="underpaid-state">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 mb-6">
                <AlertTriangle className="h-8 w-8 text-amber-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Underpaid</h3>
              <p className="text-white/40 text-sm mb-6 leading-relaxed max-w-xs mx-auto">
                Your payment has been received; however, the amount is less than the invoice total. Please contact your supplier to have the payment manually verified.
              </p>

              {/* Amount details */}
              <div className="mb-6 p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] text-left space-y-2" data-testid="underpaid-amount-details">
                <div className="flex justify-between text-sm">
                  <span className="text-white/40">Invoice Amount</span>
                  <span className="text-white/70 font-mono">{invoice.payable_amount || invoice.amount} {invoice.currency}</span>
                </div>
                {invoice.received_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-white/40">Received Amount</span>
                    <span className="text-amber-400 font-mono">{invoice.received_amount} {invoice.currency}</span>
                  </div>
                )}
              </div>

              {invoice.tx_hash && (
                <a href={`https://bscscan.com/tx/${invoice.tx_hash}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-amber-400 text-sm hover:text-amber-300 transition-colors" data-testid="view-bscscan-link">
                  View on BSCScan <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          )}

          {/* === PARTIALLY PAID STATE — allows top-up === */}
          {isPartiallyPaid && !isExpired && (
            <div className="px-7 py-8" data-testid="partially-paid-state">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 mb-4">
                  <AlertTriangle className="h-8 w-8 text-amber-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-1">Partial Payment Received</h3>
                <p className="text-white/40 text-sm">Send the remaining amount to complete your payment.</p>
              </div>

              {/* Customer Email Prompt */}
              {!emailSaved ? (
                <div className="mb-5 p-3.5 bg-white/[0.03] border border-white/[0.08] rounded-xl">
                  <p className="text-white/50 text-xs mb-2">Enter your email for payment receipts (optional):</p>
                  <div className="flex gap-2">
                    <input type="email" placeholder="your@email.com" value={customerEmail}
                      onChange={e => setCustomerEmail(e.target.value)}
                      className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:border-indigo-500/50"
                      data-testid="partial-customer-email-input" />
                    <button onClick={saveCustomerEmail}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium rounded-lg transition-colors"
                      data-testid="partial-save-email-btn">Save</button>
                  </div>
                </div>
              ) : (
                <div className="mb-5 flex items-center gap-2 px-3.5 py-2 bg-emerald-500/5 border border-emerald-500/20 rounded-xl">
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />
                  <span className="text-xs text-emerald-400">Receipts will be sent to {customerEmail}</span>
                  <button onClick={() => setEmailSaved(false)} className="ml-auto text-[10px] text-white/30 hover:text-white/50">Edit</button>
                </div>
              )}

              {/* Amount breakdown */}
              <div className="mb-6 p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] space-y-2.5" data-testid="partial-amount-details">
                <div className="flex justify-between text-sm">
                  <span className="text-white/40">Invoice Amount</span>
                  <span className="text-white/70 font-mono">{invoice.payable_amount || invoice.amount} {invoice.currency}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/40">Received So Far</span>
                  <span className="text-amber-400 font-mono">{invoice.received_amount || invoice.received_total || 0} {invoice.currency}</span>
                </div>
                <div className="h-px bg-white/[0.06]" />
                <div className="flex justify-between text-sm font-semibold">
                  <span className="text-white/60">Remaining</span>
                  <span className="text-[#6c63ff] font-mono">{invoice.remaining_amount || 0} {invoice.currency}</span>
                </div>
              </div>

              {/* Wallet address for top-up */}
              <div className="mb-4">
                <p className="text-white/50 text-sm mb-2">Send remaining to:</p>
                <div className="flex items-center gap-2 p-3 bg-white/[0.04] border border-white/[0.08] rounded-xl cursor-pointer group hover:border-white/[0.15] transition-colors"
                  onClick={() => copyToClipboard(invoice.wallet_address)} data-testid="partial-wallet-address">
                  <code className="flex-1 text-xs font-mono text-white/60 break-all">{invoice.wallet_address}</code>
                  <button className="shrink-0 p-1.5 rounded-lg hover:bg-white/[0.06] transition-colors">
                    {copied ? <CheckCircle className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4 text-white/30" />}
                  </button>
                </div>
              </div>

              {/* Submit additional TXID */}
              <div className="mb-3">
                <p className="text-white/50 text-sm mb-2">Submit additional Transaction ID</p>
                <Input
                  placeholder="0x..."
                  value={verifyTxHash}
                  onChange={(e) => setVerifyTxHash(e.target.value)}
                  className="bg-white/[0.04] border-white/[0.08] focus:border-[#6c63ff]/50 h-12 font-mono text-sm text-white/70 rounded-xl placeholder:text-white/20"
                  data-testid="partial-tx-input"
                />
              </div>
              <Button className="w-full h-14 bg-[#6c63ff] hover:bg-[#5b54e6] text-white font-semibold rounded-xl text-lg tracking-wide transition-all"
                onClick={verifyPayment} disabled={verifying || !verifyTxHash.trim()} data-testid="partial-verify-btn">
                {verifying ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
                {verifying ? 'Verifying...' : 'Verify Top-up Payment'}
              </Button>

              {/* Timer */}
              <div className="mt-4 flex items-center justify-between">
                <span className="text-white/30 text-xs">Time remaining</span>
                <span className="text-white/50 text-xs font-mono">{formatTime(timeLeft)}</span>
              </div>
            </div>
          )}

          {/* === UNPAID STATE === */}
          {isUnpaid && (
            <div className="px-7 py-10 text-center" data-testid="unpaid-state">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 mb-6">
                <XCircle className="h-8 w-8 text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Unpaid</h3>
              <p className="text-white/40 text-sm mb-6 leading-relaxed max-w-xs mx-auto">
                No payment amount detected in this transaction. Please verify the transaction ID and try again.
              </p>
            </div>
          )}

          {/* === STEP CONTENT (only when pending & not expired) === */}
          {isPending && !isExpired && (
            <div className="px-7 pb-7">

              {/* SCREEN 1: Wallet Details */}
              {step === 1 && (
                <div className="pt-6" data-testid="step-wallet-details">
                  {/* Customer Email */}
                  {!emailSaved ? (
                    <div className="mb-5 p-3.5 bg-white/[0.03] border border-white/[0.08] rounded-xl">
                      <p className="text-white/50 text-xs mb-2">Enter your email for payment receipts (optional):</p>
                      <div className="flex gap-2">
                        <input type="email" placeholder="your@email.com" value={customerEmail}
                          onChange={e => setCustomerEmail(e.target.value)}
                          className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:border-indigo-500/50"
                          data-testid="customer-email-input" />
                        <button onClick={saveCustomerEmail}
                          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium rounded-lg transition-colors"
                          data-testid="save-email-btn">Save</button>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-5 flex items-center gap-2 px-3.5 py-2 bg-emerald-500/5 border border-emerald-500/20 rounded-xl">
                      <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />
                      <span className="text-xs text-emerald-400">Receipts will be sent to {customerEmail}</span>
                      <button onClick={() => setEmailSaved(false)} className="ml-auto text-[10px] text-white/30 hover:text-white/50">Edit</button>
                    </div>
                  )}
                  {/* QR Code */}
                  {invoice.qr_code && (
                    <div className="flex justify-center mb-6">
                      <div className="p-4 bg-white rounded-xl">
                        <img src={`data:image/png;base64,${invoice.qr_code}`} alt="Payment QR Code"
                          className="w-48 h-48" data-testid="qr-code" />
                      </div>
                    </div>
                  )}

                  {/* Wallet Address */}
                  <div className="mb-5">
                    <p className="text-white/50 text-sm mb-2">Wallet Address:</p>
                    <div className="flex items-center gap-2 p-3.5 bg-white/[0.04] border border-white/[0.08] rounded-xl cursor-pointer group hover:border-white/[0.15] transition-colors"
                      onClick={() => copyToClipboard(invoice.wallet_address)} data-testid="wallet-address-container">
                      <code className="flex-1 text-sm font-mono text-white/60 break-all leading-relaxed" data-testid="wallet-address">
                        {invoice.wallet_address}
                      </code>
                      <button className="shrink-0 p-1.5 rounded-lg hover:bg-white/[0.06] transition-colors" data-testid="copy-address-btn">
                        {copied ? <CheckCircle className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4 text-white/30 group-hover:text-white/50 transition-colors" />}
                      </button>
                    </div>
                  </div>

                  {/* Network Warning */}
                  {(() => {
                    const networkLabels = { bsc: { standard: 'BEP20', name: 'BNB Smart Chain' }, tron: { standard: 'TRC20', name: 'TRON' }, ethereum: { standard: 'ERC20', name: 'Ethereum' }, polygon: { standard: 'Polygon', name: 'Polygon' } };
                    const netInfo = networkLabels[invoice?.network] || { standard: invoice?.network?.toUpperCase(), name: invoice?.network };
                    return (
                  <div className="mb-6 flex items-start gap-3 p-3.5 rounded-xl bg-white/[0.02]">
                    <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
                    <p className="text-white/50 text-sm leading-relaxed">
                      Only send <span className="text-white/70 font-medium">{invoice.currency}</span> on {netInfo.name} ({netInfo.standard}). Wrong tokens or network will result in loss of funds.
                    </p>
                  </div>
                    );
                  })()}

                  {/* Simulate Payment (Test Mode) */}
                  {invoice.is_test && (
                    <Button className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-xl text-sm mb-3"
                      onClick={simulatePayment} disabled={simulating} data-testid="simulate-payment-btn">
                      {simulating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FlaskConical className="h-4 w-4 mr-2" />}
                      {simulating ? 'Simulating...' : 'Simulate Payment'}
                    </Button>
                  )}

                  {/* Next Button - hidden in test mode */}
                  {!invoice.is_test && (
                  <Button className="w-full h-14 bg-[#6c63ff] hover:bg-[#5b54e6] text-white font-semibold rounded-xl text-lg tracking-wide transition-all"
                    onClick={() => setStep(2)} data-testid="step1-next-btn">
                    Next
                  </Button>
                  )}
                </div>
              )}

              {/* SCREEN 2: Payment Information */}
              {step === 2 && (
                <div className="pt-8 flex flex-col items-center" data-testid="step-payment-info">
                  <p className="text-white/60 text-xl mb-4">Payable Amount</p>
                  <p className="text-white text-7xl font-bold tracking-tight leading-none mb-6" data-testid="payable-amount-display">
                    {displayAmount}
                  </p>

                  {/* Warning */}
                  <div className="w-full flex items-start gap-3 p-3.5 rounded-xl bg-white/[0.02] mb-10">
                    <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
                    <p className="text-white/50 text-sm leading-relaxed">
                      To enable automatic verification, please send the exact amount shown.
                    </p>
                  </div>

                  {/* Back / Next */}
                  <div className="w-full flex gap-3">
                    <Button className="flex-1 h-14 bg-white/[0.1] hover:bg-white/[0.15] text-white font-semibold rounded-xl text-lg transition-all"
                      onClick={() => setStep(1)} data-testid="step2-back-btn">
                      Back
                    </Button>
                    <Button className="flex-1 h-14 bg-[#6c63ff] hover:bg-[#5b54e6] text-white font-semibold rounded-xl text-lg tracking-wide transition-all"
                      onClick={() => setStep(3)} data-testid="step2-next-btn">
                      Next
                    </Button>
                  </div>
                </div>
              )}

              {/* SCREEN 3: Payment Confirmation */}
              {step === 3 && (
                <div className="pt-8 flex flex-col items-center" data-testid="step-confirmation">
                  <h2 className="text-white text-3xl font-semibold mb-2">Waiting for confirmation</h2>
                  <p className="text-white/60 text-lg mb-8">Have you sent the payment?</p>

                  {/* Amount to send box */}
                  <div className="w-full mb-10">
                    <p className="text-white/50 text-sm mb-2">Amount to send</p>
                    <div className="flex items-baseline justify-center gap-3 p-5 rounded-xl bg-white/[0.04] border border-white/[0.08]">
                      <span className="text-white text-5xl font-bold tracking-tight">{displayAmount}</span>
                      <span className="text-white/50 text-xl font-medium">{invoice.currency}</span>
                    </div>
                  </div>

                  {/* Back / Yes */}
                  <div className="w-full flex gap-3">
                    <Button className="flex-1 h-14 bg-white/[0.1] hover:bg-white/[0.15] text-white font-semibold rounded-xl text-lg transition-all"
                      onClick={() => setStep(2)} data-testid="step3-back-btn">
                      Back
                    </Button>
                    <Button className="flex-1 h-14 bg-[#6c63ff] hover:bg-[#5b54e6] text-white font-semibold rounded-xl text-lg tracking-wide transition-all"
                      onClick={() => setStep(isExactAmount ? 5 : 4)} data-testid="step3-yes-btn">
                      Yes
                    </Button>
                  </div>
                </div>
              )}

              {/* SCREEN 4: Loader - Waiting for payment */}
              {step === 4 && (
                <div className="pt-8 flex flex-col items-center" data-testid="step-loader">
                  {/* Spinner */}
                  <div className="mb-6">
                    <svg className="w-40 h-40 animate-spin-slow" viewBox="0 0 160 160">
                      <circle cx="80" cy="80" r="65" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="14" strokeLinecap="round" />
                      <circle cx="80" cy="80" r="65" fill="none" stroke="#6c63ff" strokeWidth="14" strokeLinecap="round"
                        strokeDasharray="120 290" className="origin-center" />
                    </svg>
                  </div>

                  {/* Verifying text */}
                  <p className="text-white/60 text-lg font-medium mb-2 tracking-wide" data-testid="verifying-text">
                    Verifying your transaction
                  </p>

                  {!showManualVerify && !transactionDetected && (
                    <p className="text-white/30 text-sm mb-8">This may take a few minutes...</p>
                  )}

                  {/* "I have sent payment" button - shows after 3 min or when transaction detected */}
                  {(showManualVerify || transactionDetected) && (
                    <div className="w-full mt-4 mb-3 space-y-3" data-testid="manual-verify-section">
                      <p className="text-white/40 text-sm text-center mb-2">
                        {transactionDetected
                          ? 'Transaction detected. You can verify manually.'
                          : 'Payment not detected automatically? Verify with your TX ID.'}
                      </p>
                      <Button className="w-full h-14 bg-[#6c63ff] hover:bg-[#5b54e6] text-white font-semibold rounded-xl text-lg tracking-wide transition-all"
                        onClick={() => setStep(5)} data-testid="i-have-sent-payment-btn">
                        I have sent payment
                      </Button>
                    </div>
                  )}

                  {/* Back */}
                  <Button className="w-full h-14 bg-white/[0.1] hover:bg-white/[0.15] text-white font-semibold rounded-xl text-lg transition-all"
                    onClick={() => setStep(3)} data-testid="step4-back-btn">
                    Back
                  </Button>
                </div>
              )}

              {/* SCREEN 5: Verify Payment — Sender Wallet or Transaction ID */}
              {step === 5 && (
                <div className="pt-8 flex flex-col" data-testid="step-verify-payment">
                  {/* Toggle: Sender Wallet / Transaction ID */}
                  <div className="flex mb-6 p-1 bg-white/[0.04] rounded-xl border border-white/[0.08]" data-testid="verify-mode-toggle">
                    <button
                      onClick={() => setVerifyMode('sender_wallet')}
                      className={`flex-1 py-3 px-3 rounded-lg text-sm font-medium transition-all ${
                        verifyMode === 'sender_wallet'
                          ? 'bg-[#6c63ff] text-white shadow-lg'
                          : 'text-white/40 hover:text-white/60'
                      }`}
                      data-testid="verify-mode-sender-wallet"
                    >
                      Sender Wallet
                    </button>
                    <button
                      onClick={() => setVerifyMode('tx_id')}
                      className={`flex-1 py-3 px-3 rounded-lg text-sm font-medium transition-all ${
                        verifyMode === 'tx_id'
                          ? 'bg-[#6c63ff] text-white shadow-lg'
                          : 'text-white/40 hover:text-white/60'
                      }`}
                      data-testid="verify-mode-tx-id"
                    >
                      Transaction ID
                    </button>
                  </div>

                  {/* Sender Wallet Mode */}
                  {verifyMode === 'sender_wallet' && (
                    <div data-testid="verify-sender-wallet-section">
                      <p className="text-white/50 text-sm mb-2">Enter <strong className="text-white/70">your</strong> wallet address — the one you sent the payment from (not the receiving address above)</p>
                      <Input
                        placeholder="0x..."
                        value={senderWallet}
                        onChange={(e) => setSenderWallet(e.target.value)}
                        className="bg-white/[0.04] border-white/[0.08] focus:border-[#6c63ff]/50 h-12 font-mono text-sm text-white/70 rounded-xl placeholder:text-white/20 mb-3"
                        data-testid="verify-sender-wallet-input"
                      />
                      <Button
                        className="w-full h-14 bg-[#6c63ff] hover:bg-[#5b54e6] text-white font-semibold rounded-xl text-lg tracking-wide transition-all mb-16"
                        onClick={verifyBySenderWallet}
                        disabled={verifying || !senderWallet.trim()}
                        data-testid="verify-sender-wallet-btn"
                      >
                        {verifying ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
                        {verifying ? 'Verifying...' : 'Verify Payment'}
                      </Button>
                    </div>
                  )}

                  {/* Transaction ID Mode */}
                  {verifyMode === 'tx_id' && (
                    <div data-testid="verify-tx-id-section">
                      <p className="text-white/50 text-sm mb-2">Enter Transaction ID</p>
                      <Input
                        placeholder="0x..."
                        value={verifyTxHash}
                        onChange={(e) => setVerifyTxHash(e.target.value)}
                        className="bg-white/[0.04] border-white/[0.08] focus:border-[#6c63ff]/50 h-12 font-mono text-sm text-white/70 rounded-xl placeholder:text-white/20 mb-3"
                        data-testid="verify-tx-input"
                      />
                      <Button
                        className="w-full h-14 bg-[#6c63ff] hover:bg-[#5b54e6] text-white font-semibold rounded-xl text-lg tracking-wide transition-all mb-16"
                        onClick={verifyPayment}
                        disabled={verifying || !verifyTxHash.trim()}
                        data-testid="verify-payment-btn"
                      >
                        {verifying ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
                        {verifying ? 'Verifying...' : 'Submit'}
                      </Button>
                    </div>
                  )}

                  {/* Back */}
                  <Button className="w-full h-14 bg-white/[0.1] hover:bg-white/[0.15] text-white font-semibold rounded-xl text-lg transition-all"
                    onClick={() => setStep(isExactAmount ? 3 : 4)} data-testid="step5-back-btn">
                    Back
                  </Button>
                </div>
              )}

            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-1.5 mt-8">
          <Shield className="h-3 w-3 text-white/15" />
          <span className="text-white/15 text-xs tracking-wide">Powered by CoinPayUp</span>
        </div>
      </div>

      {/* Custom animation for the loader spinner */}
      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 2s linear infinite;
        }
      `}</style>
    </div>
  );
};

/* Status pill matching the design */
const StatusPill = ({ status, isExpired }) => {
  if (status === 'paid') {
    return (
      <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.06]" data-testid="status-badge">
        <div className="w-2 h-2 rounded-full bg-emerald-400" />
        <span className="text-white/70 text-sm font-medium">Paid</span>
      </div>
    );
  }
  if (status === 'overpaid') {
    return (
      <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.06]" data-testid="status-badge">
        <div className="w-2 h-2 rounded-full bg-emerald-400" />
        <span className="text-white/70 text-sm font-medium">Overpaid</span>
      </div>
    );
  }
  if (status === 'partially_paid') {
    return (
      <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.06]" data-testid="status-badge">
        <div className="w-2 h-2 rounded-full bg-amber-400" />
        <span className="text-white/70 text-sm font-medium">Partially Paid</span>
      </div>
    );
  }
  if (status === 'underpaid') {
    return (
      <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.06]" data-testid="status-badge">
        <div className="w-2 h-2 rounded-full bg-amber-400" />
        <span className="text-white/70 text-sm font-medium">Underpaid</span>
      </div>
    );
  }
  if (status === 'unpaid') {
    return (
      <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.06]" data-testid="status-badge">
        <div className="w-2 h-2 rounded-full bg-red-400" />
        <span className="text-white/70 text-sm font-medium">Unpaid</span>
      </div>
    );
  }
  if (status === 'expired' || isExpired) {
    return (
      <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.06]" data-testid="status-badge">
        <div className="w-2 h-2 rounded-full bg-red-400" />
        <span className="text-white/70 text-sm font-medium">Expired</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.06]" data-testid="status-badge">
      <div className="w-2 h-2 rounded-full bg-amber-400" />
      <span className="text-white/70 text-sm font-medium">Pending</span>
    </div>
  );
};

export default CheckoutPage;
